import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Payment, Installment } from '../../../../core/generated';
import { ExternalPayerService } from '../services/external-payer.service';
import { fadeIn, listStagger, expandCollapse } from '../../shared/animations';

interface PaymentWithInstallments extends Payment {
  installmentsLoaded?: boolean;
  isExpanded?: boolean;
  isLoadingInstallments?: boolean;
}

@Component({
  selector: 'app-payer-installments',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './payer-installments.component.html',
  styleUrl: './payer-installments.component.scss',
  animations: [fadeIn, listStagger, expandCollapse]
})
export class PayerInstallmentsComponent implements OnInit {
  @Input() userCpf!: string;

  private externalPayerService = inject(ExternalPayerService);
  private snackBar = inject(MatSnackBar);

  payments = signal<PaymentWithInstallments[]>([]);
  isLoading = signal(false);
  processingInstallmentId = signal<number | null>(null);

  ngOnInit(): void {
    this.loadPayments();
  }

  loadPayments(): void {
    if (!this.userCpf) return;
    this.isLoading.set(true);
    this.externalPayerService.getPayerPaymentsByUserCpf(this.userCpf).subscribe({
      next: (data) => { this.payments.set(data); this.isLoading.set(false); },
      error: () => {
        this.snackBar.open('Erro ao carregar pagamentos.', 'Fechar', { duration: 4000 });
        this.isLoading.set(false);
      }
    });
  }

  toggleInstallments(payment: PaymentWithInstallments): void {
    payment.isExpanded = !payment.isExpanded;
    if (payment.isExpanded && !payment.installmentsLoaded) {
      this.loadInstallments(payment);
    }
  }

  private loadInstallments(payment: PaymentWithInstallments): void {
    if (!payment.id) return;
    payment.isLoadingInstallments = true;
    this.externalPayerService.getInstallments(payment.id).subscribe({
      next: (installments) => {
        payment.installments = installments;
        payment.installmentsLoaded = true;
        payment.isLoadingInstallments = false;
        this.payments.update(list => [...list]);
      },
      error: () => {
        this.snackBar.open('Erro ao carregar parcelas.', 'Fechar', { duration: 4000 });
        payment.isLoadingInstallments = false;
      }
    });
  }

  onPayInstallment(installment: Installment): void {
    if (!installment.id) return;
    this.processingInstallmentId.set(installment.id);
    this.externalPayerService.payInstallment(installment.id).subscribe({
      next: () => {
        this.snackBar.open('Parcela paga com sucesso!', 'Fechar', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.processingInstallmentId.set(null);
        this.loadPayments();
      },
      error: () => {
        this.snackBar.open('Erro ao pagar parcela.', 'Fechar', { duration: 4000 });
        this.processingInstallmentId.set(null);
      }
    });
  }

  canPayInstallment(installment: Installment): boolean {
    return installment.status === 'PENDING_PAYMENT' || installment.status === 'LATE';
  }

  isProcessingInstallment(id?: number): boolean {
    return this.processingInstallmentId() === id;
  }

  getInstallmentStatusClass(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'badge-warning',
      'PAID': 'badge-success',
      'LATE': 'badge-error',
      'CANCELLED_PAYMENT': 'badge-cancelled',
    };
    return map[status ?? ''] ?? 'badge-primary';
  }

  getInstallmentStatusLabel(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'Pendente',
      'PAID': 'Pago',
      'LATE': 'Atrasado',
      'CANCELLED_PAYMENT': 'Cancelado',
    };
    return map[status ?? ''] ?? status ?? '';
  }

  getPaymentStatusClass(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'badge-warning',
      'PARTIALLY_PAID': 'badge-primary',
      'PAID': 'badge-success',
      'FAILED': 'badge-error',
    };
    return map[status ?? ''] ?? 'badge-primary';
  }

  getPaymentStatusLabel(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'Pendente',
      'PARTIALLY_PAID': 'Parcialmente Pago',
      'PAID': 'Pago',
      'FAILED': 'Falhou',
    };
    return map[status ?? ''] ?? status ?? '';
  }

  getPaymentMethodLabel(method?: string): string {
    const map: Record<string, string> = {
      'PIX': 'PIX',
      'BOLETO': 'Boleto',
      'CREDIT_CARD': 'Cartão de Crédito',
      'BANK_TRANSFER': 'Transferência',
    };
    return map[method ?? ''] ?? method ?? 'N/A';
  }

  formatCurrency(value?: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  }
}
